<template>
  <div :id="id" class="variable-badge" contenteditable="false">
    <VDropdown placement="bottom" style="display: inline-block" :shown="isOpen" :disabled="invalid">
      <n-tooltip position="top" :text="getError()">
        <span
          class="pill"
          :class="{ error: invalid }"
          :data-fallback="fallbackValue"
          :data-scope="variable.scope"
          :data-key="variable.key"
          @click="isOpen = true"
        >
          <span class="icon">
            <Icon v-if="variable.scope === 'flow'" icon="FlowVariableIcon"></Icon>
            <Icon class="material-badge-icon" v-if="variable.scope !== 'flow'" :icon="icon"></Icon>
          </span>
          {{ variable.name }}
        </span>
      </n-tooltip>

      <template #popper>
        <div class="field">
          <label class="fallback-text label">Fallback text will be used if the variable is not set</label>
          <div class="control">
            <input
              class="tooltip-content input"
              type="text"
              placeholder="Enter fallback text here"
              required
              v-model.trim="fallbackValue"
              @keypress.enter="closeFallback"
            />
          </div>
        </div>
      </template>
    </VDropdown>
  </div>
</template>

<script lang="ts" setup>
import { InputVariableViewModel } from '../../../models/designer';
import Icon from '@/shared/icons/Icon.vue';
import AttributeIconUtil from '../../../../../shared/util/attributeIconUtil';
import { onBeforeMount, PropType, ref, computed } from 'vue';
import { Icons, IconType } from '@/shared/icons/icons';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  variable: {
    type: Object as PropType<InputVariableViewModel>,
    required: true,
  },
  fallback: {
    type: String,
    default: '',
  },
});

const emit = defineEmits<{
  (e: 'closedFallback'): void;
}>();

defineExpose({
  update,
});

const fallbackValue = ref('');
const isOpen = ref(false);
const invalid = ref(false);

onBeforeMount(() => {
  fallbackValue.value = props.fallback;
  invalid.value = !isValid();
});

function update() {
  invalid.value = !isValid();
}

const icon = computed<IconType>(() => {
  if (invalid.value) {
    return 'ErrorIcon';
  }

  return AttributeIconUtil.getIconType(props.variable.dataType, props.variable.key, false);
});

function getIconComponent() {
  return Icons[getIcon()];
}

function getIcon(): IconType {
  if (invalid.value) {
    return 'ErrorIcon';
  }

  return AttributeIconUtil.getIconType(props.variable.dataType, props.variable.key, false) || '';
}

function isValid(): boolean {
  return !props.variable.error;
}

function closeFallback() {
  isOpen.value = false;
  emit('closedFallback');
}

function getError() {
  if (props.variable.error) {
    return props.variable.error;
  }
}
</script>

<style scoped lang="scss">
.variable-badge {
  display: inline-block;
}

.pill {
  background-color: #eeeeee;
  color: black;
  padding: 0px 5px 0px 1px;

  text-align: center;
  text-decoration: none;
  display: inline-flex;

  cursor: pointer;
  border-radius: 5px;
  align-items: center;
  font-size: 0.8em;
  &:hover {
    background-color: #e3e3e3;
  }
}

.error {
  background: #ffd7df;
  &:hover {
    background-color: #fac0cc;
  }
}

.label {
  font-size: 1em;
  color: white;
}
.input {
  font-size: 0.75em;

  color: black;
  &::placeholder {
    color: #595959;
  }
}

.material-badge-icon {
  top: -1px; // the component icons are 1 pixel out
}
.person-icon {
  width: 11px;
  height: 13px;
  position: relative;
  padding: 0;
}
</style>
